import api from "./axios";
import type { Permission } from "@/types/auth";

export type { Permission };

export interface PermissionFormData {
    name: string;
    guard_name: string;
    group_name?: string;
}

export interface PermissionListResponse {
    success: boolean;
    message: string;
    data: {
        items: Permission[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
}

export interface PermissionResponse {
    success: boolean;
    message: string;
    data: Permission;
}

export interface PermissionGroupResponse {
    success: boolean;
    data: string[];
}

export interface PermissionByGroupResponse {
    success: boolean;
    data: {
        [group: string]: Permission[];
    };
}

const permissionApi = {
    // Get all permissions with pagination
    getPermissions: async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        group?: string;
    }): Promise<PermissionListResponse> => {
        const response = await api.get("/v1/permissions", { params });
        return response.data;
    },

    // Get all permissions without pagination
    getAllPermissions: async (): Promise<{
        success: boolean;
        message: string;
        data: Permission[];
    }> => {
        const response = await api.get("/v1/permissions/all");
        return response.data;
    },

    // Get permission by ID
    getPermission: async (id: number): Promise<PermissionResponse> => {
        const response = await api.get(`/v1/permissions/${id}`);
        return response.data;
    },

    // Create new permission
    createPermission: async (
        data: PermissionFormData,
    ): Promise<PermissionResponse> => {
        const response = await api.post("/v1/permissions", data);
        return response.data;
    },

    // Update permission
    updatePermission: async (
        id: number,
        data: PermissionFormData,
    ): Promise<PermissionResponse> => {
        const response = await api.put(`/v1/permissions/${id}`, data);
        return response.data;
    },

    // Delete permission
    deletePermission: async (id: number): Promise<{ success: boolean }> => {
        const response = await api.delete(`/v1/permissions/${id}`);
        return response.data;
    },

    // Get permission groups
    getPermissionGroups: async (): Promise<PermissionGroupResponse> => {
        const response = await api.get("/v1/permission-groups");
        return response.data;
    },

    // Get permissions grouped by group
    getPermissionsByGroup: async (): Promise<PermissionByGroupResponse> => {
        const response = await api.get("/v1/permissions/by-group");
        return response.data;
    },
};

export default permissionApi;
